// src/components/EnginePowerGraph.js
import React, { useState, useEffect, useRef } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { useAuthState } from 'react-firebase-hooks/auth';
import MobileScrollInput from './MobileScrollInput';
import '../styles.css';

function EnginePowerGraph({ params, sessionId, vehicleId, eventId, powerData, onPowerDataChange }) {
  const [user, loading, error] = useAuthState(auth);
  const [showFullScreen, setShowFullScreen] = useState(false);
  const [vehicleName, setVehicleName] = useState('Unnamed Vehicle');
  const [eventName, setEventName] = useState('Unnamed Event');
  const [sessionName, setSessionName] = useState('Unnamed Session');
  const [syncStatus, setSyncStatus] = useState('Loading...');
  const svgRef = useRef(null);

  // Load vehicle, event, and session names
  useEffect(() => {
    const loadData = async () => {
      if (!user || !vehicleId || !eventId) {
        console.warn('Missing user, vehicleId, or eventId for EnginePowerGraph');
        setSyncStatus('Error: Missing data');
        return;
      }
      try {
        setSyncStatus('Loading...');
        let sessionData = null, vehicleData = null, eventData = null;
        // Fetch session (if sessionId exists)
        if (sessionId) {
          const sessionDoc = await getDoc(doc(db, `users/${user.uid}/sessions/${sessionId}`));
          sessionData = sessionDoc.exists() ? sessionDoc.data() : null;
        }
        // Fetch vehicle and event
        const vehicleDoc = await getDoc(doc(db, `users/${user.uid}/vehicles/${vehicleId}`));
        vehicleData = vehicleDoc.exists() ? vehicleDoc.data() : null;
        const eventDoc = await getDoc(doc(db, `users/${user.uid}/events/${eventId}`));
        eventData = eventDoc.exists() ? eventDoc.data() : null;

        setSessionName(sessionData?.sessionName || 'Unnamed Session');
        setVehicleName(vehicleData?.name || 'Unnamed Vehicle');
        setEventName(eventData?.name || 'Unnamed Event');
        setSyncStatus(navigator.onLine ? 'Synced' : 'Offline (cached)');
      } catch (err) {
        console.error('Error fetching data for EnginePowerGraph:', err.message);
        setSyncStatus(`Error: ${err.message}`);
      }
    };
    if (!loading && user) loadData();

    // Update sync status on connectivity
    const updateSyncStatus = () => {
      setSyncStatus(navigator.onLine ? 'Synced' : 'Offline (cached)');
    };
    window.addEventListener('online', updateSyncStatus);
    window.addEventListener('offline', updateSyncStatus);
    return () => {
      window.removeEventListener('online', updateSyncStatus);
      window.removeEventListener('offline', updateSyncStatus);
    };
  }, [user, loading, sessionId, vehicleId, eventId]);

  // Extract parameters
  const revLimit = parseFloat(params['Rev limit']) || 8000;

  // Handle table input changes
  const handleInputChange = (index, field, value) => {
    const updatedData = [...powerData];
    updatedData[index] = { ...updatedData[index], [field]: value };
    onPowerDataChange(updatedData);
  };

  const addRow = () => {
    onPowerDataChange([...powerData, { rpm: '', torque: '' }]);
  };

  // Calculate horsepower from torque and RPM
  const calculateHorsepower = (torque, rpm) => {
    if (!torque || !rpm) return 0;
    const parsedTorque = parseFloat(torque);
    const parsedRpm = parseFloat(rpm);
    if (isNaN(parsedTorque) || isNaN(parsedRpm)) return 0;
    return (parsedTorque * parsedRpm) / 5252; // HP = (Torque * RPM) / 5252
  };

  // Filter valid data points
  const validData = powerData
    .filter(d => d.rpm && d.torque && !isNaN(parseFloat(d.rpm)) && !isNaN(parseFloat(d.torque)))
    .map(d => ({
      rpm: parseFloat(d.rpm),
      torque: parseFloat(d.torque),
      horsepower: calculateHorsepower(d.torque, d.rpm)
    }))
    .sort((a, b) => a.rpm - b.rpm);

  // SVG constants
  const SVG_WIDTH = 600;
  const SVG_HEIGHT = 500;
  const MARGIN = { top: 30, right: 20, bottom: 50, left: 70 };
  const FULL_SCREEN_MARGIN = { top: 30, right: 20, bottom: 60, left: 65}; 
  const GRAPH_WIDTH = SVG_WIDTH - MARGIN.left - MARGIN.right;
  const GRAPH_HEIGHT = SVG_HEIGHT - MARGIN.top - MARGIN.bottom;
  const FULL_SCREEN_GRAPH_HEIGHT = SVG_HEIGHT - FULL_SCREEN_MARGIN.top - FULL_SCREEN_MARGIN.bottom;

  // Scales
  const maxRpm = Math.ceil(revLimit / 1000) * 1000;
  const maxTorque = validData.length ? Math.max(...validData.map(d => d.torque), 100) : 100;
  const maxHorsepower = validData.length ? Math.max(...validData.map(d => d.horsepower), 100) : 100;
  const maxY = Math.max(maxTorque, maxHorsepower);
  const xScale = (rpm) => (rpm / maxRpm) * GRAPH_WIDTH;
  const yScale = (value, height = GRAPH_HEIGHT) => {
    return height - (value / maxY) * height;
  };

  // Generate X-axis ticks (every 2000 RPM)
  const xTicks = [];
  for (let rpm = 2000; rpm <= maxRpm; rpm += 2000) {
    xTicks.push(rpm);
  }

  // Generate Y-axis ticks (dynamic based on maxY)
  const yTicks = [];
  const yStep = Math.ceil(maxY / 5 / 50) * 50; // Round to nearest 50
  for (let value = 0; value <= maxY; value += yStep) {
    yTicks.push(value);
  }

  // Generate paths with Bezier curves
  const generatePath = (data, key, yScale) => {
    if (data.length < 2) return '';
    const points = data.map(d => ({ x: xScale(d.rpm), y: yScale(d[key]) }));
    let pathD = `M ${points[0].x},${points[0].y}`;
    for (let i = 1; i < points.length - 1; i++) {
      const c1 = points[i - 1];
      const c2 = points[i];
      const c3 = points[i + 1];
      const c1c2Dist = Math.hypot(c2.x - c1.x, c2.y - c1.y);
      const c2c3Dist = Math.hypot(c3.x - c2.x, c3.y - c2.y);
      const radius = 20;
      const effectiveRadius = Math.min(radius, c1c2Dist / 2, c2c3Dist / 2);
      const c1c2Ratio = (c1c2Dist - effectiveRadius) / c1c2Dist;
      const c1c2Curve = {
        x: (1 - c1c2Ratio) * c1.x + c1c2Ratio * c2.x,
        y: (1 - c1c2Ratio) * c1.y + c1c2Ratio * c2.y
      };
      const c2c3Ratio = effectiveRadius / c2c3Dist;
      const c2c3Curve = {
        x: (1 - c2c3Ratio) * c2.x + c2c3Ratio * c3.x,
        y: (1 - c2c3Ratio) * c2.y + c2c3Ratio * c3.y
      };
      pathD += ` L ${c1c2Curve.x},${c1c2Curve.y} Q ${c2.x},${c2.y} ${c2c3Curve.x},${c2c3Curve.y}`;
    }
    pathD += ` L ${points[points.length - 1].x},${points[points.length - 1].y}`;
    return pathD;
  };





  // Render graph
  const renderGraph = (isFullScreen = false) => {
    const margin = isFullScreen ? FULL_SCREEN_MARGIN : MARGIN;
    const graphHeight = isFullScreen ? FULL_SCREEN_GRAPH_HEIGHT : GRAPH_HEIGHT;
    const localYScale = (value) => yScale(value, graphHeight);

    // Generate paths with correct scale
    const torquePath = generatePath(validData, 'torque', localYScale);
    const horsepowerPath = generatePath(validData, 'horsepower', localYScale);

    return (
      <g transform={`translate(${margin.left},${margin.top})`}>
        {/* Axes */}
        <line
          x1="0"
          y1={graphHeight}
          x2={GRAPH_WIDTH}
          y2={graphHeight}
          stroke="#333"
          strokeWidth="2"
        />
        <line
          x1="0"
          y1="0"
          x2="0"
          y2={graphHeight}
          stroke="#333"
          strokeWidth="2"
        />
        {/* Axis Labels */}
        <text x={GRAPH_WIDTH / 2} y={graphHeight + 40} textAnchor="middle" className="graph-label">
          RPM (x1000)
        </text>
        <text
          x={-graphHeight / 2}
          y={-50}
          textAnchor="middle"
          transform="rotate(-90)"
          className="graph-label"
        >
          Torque (lb-ft) / Power (HP)
        </text>
        {/* Grid Lines */}
        {xTicks.map((rpm, i) => (
          <line
            key={`x-grid-${i}`}
            x1={xScale(rpm)}
            y1="0"
            x2={xScale(rpm)}
            y2={graphHeight}
            stroke="#ccc"
            strokeDasharray="2,2"
          />
        ))}
        {yTicks.map((value, i) => (
          <line
            key={`y-grid-${i}`}
            x1="0"
            y1={localYScale(value)}
            x2={GRAPH_WIDTH}
            y2={localYScale(value)}
            stroke="#ccc"
            strokeDasharray="2,2"
          />
        ))}
        {/* Torque and Horsepower Lines */}
        {torquePath && (
          <path
            d={torquePath}
            fill="none"
            stroke="#FF4500"
            strokeWidth="3"
            strokeLinecap="round"
          />
        )}
        {horsepowerPath && (
          <path
            d={horsepowerPath}
            fill="none"
            stroke="#1E90FF"
            strokeWidth="3"
            strokeLinecap="round"
          />
        )}
        {/* Axis Tick Labels */}
        {xTicks.map((rpm, i) => (
          <text
            key={`x-tick-${i}`}
            x={xScale(rpm)}
            y={graphHeight + 20}
            textAnchor="middle"
            className="tick-label"
          >
            {(rpm / 1000).toFixed(0)}
          </text>
        ))}
        {yTicks.map((value, i) => (
          <text
            key={`y-tick-${i}`}
            x={-10}
            y={localYScale(value) + 5}
            textAnchor="end"
            className="tick-label"
          >
            {value.toFixed(0)}
          </text>
        ))}
      </g>
    );
  };

  return (
    <>
      <div className="tab-group">
        <div className="tab-group-title-container">
          <h3 className="tab-group-title">Power Graph</h3>
          <button onClick={() => setShowFullScreen(true)} className="graph-action-btn" title="Expand">
            <i className="material-icons">open_in_full</i>
          </button>
        </div>
        <div className="power-graph-container">
          <svg
            viewBox={`0 0 ${SVG_WIDTH} ${SVG_HEIGHT}`}
            preserveAspectRatio="xMidYMid meet"
            className="power-graph"
            ref={svgRef}
          >
            {renderGraph()}
          </svg>
        </div>
        <div className="power-data-table">
          <table>
            <thead>
              <tr>
                <th>RPM</th>
                <th>Torque (lb-ft)</th>
                <th>Power (HP)</th>
              </tr>
            </thead>
            <tbody>
              {powerData.map((data, index) => (
                <tr key={index}>
                  <td>
                    <MobileScrollInput
                      value={data.rpm}
                      onChange={(val) => handleInputChange(index, 'rpm', val)}
                      step="100"
                      min="0"
                      max={revLimit.toString()}
                      unit="rpm"
                      id={`rpm-${index}`}
                    />
                  </td>
                  <td>
                    <MobileScrollInput
                      value={data.torque}
                      onChange={(val) => handleInputChange(index, 'torque', val)}
                      step="1"
                      min="0"
                      max="1000"
                      unit="lb-ft"
                      id={`torque-${index}`}
                    />
                  </td>
                  <td>{calculateHorsepower(data.torque, data.rpm).toFixed(1)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <button onClick={addRow} className="btn btn-group-action add">+</button>
        </div>
      </div>
      {showFullScreen && (
        <div className="overlay-card">
          <div className="card-container power-calculator-fullscreen">
            <div className="page-title">
              <h2>Power Graph</h2>
              <div>
                <button onClick={() => setShowFullScreen(false)} className="close-button">✕</button>
              </div>
            </div>
            <div className="power-info">
              <p><strong>Vehicle:</strong> {vehicleName}</p>
              <p><strong>Event:</strong> {eventName}</p>
              <p><strong>Session:</strong> {sessionName}</p>
              <p><strong>Rev Limit:</strong> {revLimit} RPM</p>
            </div>
            <div className="power-graph-container">
              <svg
                viewBox={`0 0 ${SVG_WIDTH} ${SVG_HEIGHT}`}
                preserveAspectRatio="xMidYMid meet"
                className="power-graph"
                ref={svgRef}
              >
                {renderGraph(true)}
              </svg>
            </div>
            <div className="power-data-table">
              <table>
                <thead>
                  <tr>
                    <th>RPM</th>
                    <th>Torque (lb-ft)</th>
                    <th>Power (HP)</th>
                  </tr>
                </thead>
                <tbody>
                  {powerData.map((data, index) => (
                    <tr key={index}>
                      <td>
                        <MobileScrollInput
                          value={data.rpm}
                          onChange={(val) => handleInputChange(index, 'rpm', val)}
                          step="100"
                          min="0"
                          max={revLimit.toString()}
                          unit="rpm"
                          id={`rpm-full-${index}`}
                        />
                      </td>
                      <td>
                        <MobileScrollInput
                          value={data.torque}
                          onChange={(val) => handleInputChange(index, 'torque', val)}
                          step="1"
                          min="0"
                          max="1000"
                          unit="lb-ft"
                          id={`torque-full-${index}`}
                        />
                      </td>
                      <td>{calculateHorsepower(data.torque, data.rpm).toFixed(1)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <button onClick={addRow} className="btn btn-group-action add">+</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default EnginePowerGraph;